/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */

/**
 * Provides a convenience function which manages preloading external images.
 *
 * Generally, this API is to be used when a component needs to delay rendering
 * until the image has completed loaded. If the image just needs to be eagerly
 * loaded prior to the component being rendered, the `loadImage` API should be
 * used instead.
 *
 * ```typescript
 *   import preloadImage from '@ion/dls/src/img/ImageLoader';
 *   ...
 *
 *   const image = await preloadImage('path/to/image.jpg');
 *
 *   // multiple images can be loaded ...
 *   const image = await preloadImage('./image-1.jpg', './image-2.jpg');
 * ```
 *
 * @param   src The source path(s) from which the image is to be loaded.
 * @returns A promise which resolves to the preloaded image(s), or rejects
 *          if the source image(s) can not be retrieved.
 */
export const preloadImage = (...paths: string[]): Promise<HTMLImageElement>[] => {
  const loaders = paths?.map((path: string) => (
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onerror = img.onabort = () => reject();
      img.onload  = () => resolve(img);
      img.src = path;
    })
  ));
  return Promise.all(loaders) as unknown as Promise<HTMLImageElement>[];
};

/**
 * Provides a convenience function which allows for eagerly loading external images.
 *
 * Generally, this API is to be used when a component will need to render an image
 * and the image can be loaded eagerly prior to the component being rendered. If
 * a component needs to delay rendering until the image has completed loading,
 * the `preloadImage` API can be used instead.
 *
 * ```typescript
 *   import loadImage from '@ion/dls/src/img/ImageLoader';
 *   ...
 *
 *   loadImage('path/image-1.jpg', 'path/image-2.jpg');
 *
 *   // multiple images can be loaded ...
 *   loadImage('./image-1.jpg', './image-2.jpg');
 * ```
 *
 * @param src The source path(s) from which the image is to be loaded.
 */
export const loadImage = (...paths: string[]) => (
  paths?.forEach(path => {
    new Image().src = path;
  })
);
